// CONNECT PROCORE, THREE WAYS (owner 2026-10-21: "give me an option to connect using
// token from my browser or the same way skool connection on prigmar is doing it"):
//  - Token from my browser: paste the Authorization (Bearer ...) or Cookie header of a
//    request the Procore page makes; the server checks it against Procore first.
//  - Browser connector: the BidLight Procore Connector extension pairs itself from this
//    page and sends the signed-in procore.com session - Prigmar's Skool cookie sync.
//  - Procore sign-in: OAuth, only when the server has the Procore app keys.
// openProcoreConnect() mounts the window on its own, so any button can open it.
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Modal, Input, Segmented } from "antd";
import addNotification from "helpers/notify";
import { url, tokenRef } from "api";
import { connectProcoreToken, getProcoreConnectUrl } from "./integrationsApi";
import { refreshProviderStatus } from "./useIntegrations";

async function downloadExtension() {
  try {
    const r = await fetch(`${url}/procore/extension.zip`, { headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` } });
    if (!r.ok) throw new Error(`The download was refused (${r.status})`);
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "bidlight-procore-connector.zip";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 10000);
  } catch (e) {
    addNotification(e.message || "Could not download the connector", "danger");
  }
}

function ProcoreConnectModal({ oauth, onClose, connection = "" }) {
  const [way, setWay] = useState("token");
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(false);

  // browser connector: watch for the extension to connect the account
  useEffect(() => {
    if (way !== "extension" || !waiting) return undefined;
    const t = setInterval(async () => {
      const s = await refreshProviderStatus("procore");
      if (s && s.connected) {
        addNotification(`Procore connected${s.email ? ` as ${s.email}` : ""}`, "success");
        onClose();
      }
    }, 4000);
    return () => clearInterval(t);
  }, [way, waiting, onClose]);

  const saveToken = async () => {
    setBusy(true);
    try {
      const s = await connectProcoreToken(token, connection);
      addNotification(`Procore connected${s.email ? ` as ${s.email}` : ""}`, "success");
      await refreshProviderStatus("procore");
      onClose();
    } catch (e) {
      addNotification(e.message, "danger");
    } finally {
      setBusy(false);
    }
  };

  const signIn = async () => {
    try {
      const r = await getProcoreConnectUrl(connection);
      if (r && r.url) window.location.href = r.url;
    } catch (e) {
      addNotification(e.message || "Could not start the Procore sign-in", "danger");
    }
  };

  // the connector extension pairs the browser with the company's own account, so an
  // ADDITIONAL account is connected with a token or the sign-in
  const options = [{ value: "token", label: "Token from my browser" }];
  if (!connection) options.push({ value: "extension", label: "Browser connector" });
  if (oauth) options.push({ value: "oauth", label: "Procore sign-in" });

  return (
    <Modal open title="Connect Procore" onCancel={onClose} footer={null} width={640} destroyOnClose>
      <Segmented block options={options} value={way} onChange={setWay} className="mb-3" />
      {way === "token" ? (
        <div>
          <ol className="pl-3 mb-2">
            <li>
              Open <a href="https://app.procore.com" target="_blank" rel="noopener noreferrer">app.procore.com</a> in this browser, signed in, on any
              project&apos;s Documents page.
            </li>
            <li>Press F12, open the Network tab, and reload the page.</li>
            <li>
              Click any request whose path starts with <code>/rest/</code>, then under Request Headers copy the value of{" "}
              <b>Authorization</b> (it starts with <code>Bearer</code>). If there is none, copy the <b>Cookie</b> value instead.
            </li>
            <li>Paste it here.</li>
          </ol>
          <Input.TextArea
            rows={4}
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Bearer eyJ...   or   _procore_session=...; ..."
            spellCheck={false}
          />
          <small className="text-muted d-block mt-1">
            BidLight checks it with Procore before saving it, and acts in Procore as you. A token stops working when Procore ends that
            session; paste a new one then, or use the browser connector, which keeps it fresh.
          </small>
          <button className="header-btn text-white bg-bid border-bid px-3 mt-3" disabled={busy || !token.trim()} onClick={saveToken}>
            {busy ? "Checking with Procore..." : "Connect"}
          </button>
        </div>
      ) : null}
      {way === "extension" ? (
        <div>
          <p className="mb-2">
            The BidLight Procore Connector uses the Procore session already signed in in this browser and keeps it fresh, so the
            connection does not lapse when a token expires. It never sees your Procore password.
          </p>
          <ol className="pl-3 mb-2">
            <li>
              <button className="integration-link-btn" style={{ marginLeft: 0 }} onClick={downloadExtension}>
                Download the connector
              </button>{" "}
              and unzip it.
            </li>
            <li>
              Open <code>chrome://extensions</code> (or <code>edge://extensions</code>), turn on Developer mode, click{" "}
              <b>Load unpacked</b> and pick the unzipped folder.
            </li>
            <li>
              Sign in to <a href="https://app.procore.com" target="_blank" rel="noopener noreferrer">Procore</a> in this browser.
            </li>
            <li>Reload this page. The connector pairs itself and BidLight shows Procore as connected.</li>
          </ol>
          <button className="header-btn text-white bg-bid border-bid px-3 mt-2" disabled={waiting} onClick={() => setWaiting(true)}>
            {waiting ? "Waiting for the connector..." : "I've installed it - check now"}
          </button>
        </div>
      ) : null}
      {way === "oauth" ? (
        <div>
          <p>Sign in on Procore&apos;s own page; you come back here connected.</p>
          <button className="header-btn text-white bg-bid border-bid px-3" onClick={signIn}>
            Sign in with Procore
          </button>
        </div>
      ) : null}
    </Modal>
  );
}

/** Open the connect window from anywhere. `status` is the /procore/status answer, if known. */
//
// { connection: 'new' } connects an ADDITIONAL Procore account for the company (owner
// 2026-10-24: several accounts per company); an id reconnects that one.
export function openProcoreConnect(status, { connection = "" } = {}) {
  const el = document.createElement("div");
  document.body.appendChild(el);
  const close = () => {
    ReactDOM.unmountComponentAtNode(el);
    el.remove();
  };
  ReactDOM.render(<ProcoreConnectModal oauth={!!(status && status.oauthConfigured)} connection={connection} onClose={close} />, el);
}
